import { computed, action, observable } from 'mobx';
import { emit, events } from 'elc-service-bus';
import { ViewController } from 'elc-service';
import { serviceNames, diContainer } from '../../service-setup/diContainer';
import { IItemsParams, IItem, ItemTypes, ProdcatApiSdk } from '../../api/ProdcatApiSdk';
import { ProductRepository } from '../repositories/ProductRepository';
import { Product } from '../domain/entities/Product';
import {
    IProductCrossSell,
    IProductCrossSellConfig
} from '../../exported/product-cross-sell/ProductCrossSell';
import { PopulateProductsFromServerState } from '../use-cases/PopulateProductsFromServerState';

interface IUniqueCrossSellSku {
    [key: string]: IItem;
}

export interface IProductCrossellViewControllerData {
    crossSellProducts: Product[];
    isLoaded: boolean;
}

export class ProductCrossSellViewController extends ViewController {
    @observable private crossSellProducts: Product[];
    private productRepository: ProductRepository;
    private params: IItemsParams = { items: [] };
    public dataState: IProductCrossellViewControllerData;
    constructor() {
        super();

        this.productRepository = diContainer.get(serviceNames.productRepository);
    }

    @computed
    public get data(): IProductCrossellViewControllerData {
        return {
            crossSellProducts: this.getCrossSellProducts(),
            isLoaded: Boolean(this.crossSellProducts)
        };
    }
    @action
    private setCrossSellProducts = (crossSellProducts: Product[]) => {
        this.crossSellProducts = crossSellProducts;
    };

    private getCrossSellProducts = () =>
        this.params.items.reduce((productAcc: Product[], item) => {
            const crossProducts = this.productRepository
                .getProductById(item.params.id)
                .crossSell.reduce((crossAcc: Product[], { skuId }) => {
                    if (skuId) {
                        const crossSellProduct = this.productRepository.getProductBySkuId(skuId);
                        if (crossSellProduct) {
                            crossAcc.push(crossSellProduct);
                        }
                    }

                    return crossAcc;
                }, []);

            return [...productAcc, ...crossProducts];
        }, []);

    private getProductsFromItems = (items: IItem[]) =>
        items.filter(({ type }) => type === ItemTypes.product);

    @computed
    private get uniqueSkus() {
        const uniqueCrossSellSkus: IUniqueCrossSellSku = {};

        this.productRepository.products.forEach((product) => {
            product.crossSell.forEach(({ skuId }) => {
                if (skuId) {
                    uniqueCrossSellSkus[skuId] = {
                        type: ItemTypes.sku,
                        params: { id: skuId }
                    };
                }
            });
        });

        return Object.values(uniqueCrossSellSkus);
    }

    private loadProducts = async (items: IItem[], includeInnerHits: boolean) => {
        if (items.length > 0) {
            await this.productRepository.getProductsByItems(items, includeInnerHits);
        }
    };

    @action
    public loaded = async (props: IProductCrossSell) => {
        this.isLoading = true;
        this.setupParams(props.config);

        // loading initial products
        await this.loadProducts(this.params.items, true);

        // loading final cross sell products based on unique skuids
        await this.loadProducts(this.uniqueSkus, false);

        if (this.productRepository.products.length > 0) {
            this.setCrossSellProducts(this.data.crossSellProducts);

            const productIds = this.productRepository.products.map((product) => product.productId);

            if (productIds.length > 0) {
                emit(events.PRODUCT_LOADED, productIds);
            }
        }
        this.isLoading = false;
    };

    @action
    public handleState = (config: IProductCrossSellConfig) => {
        if (!this.dataState) {
            return;
        }
        try {
            this.setupParams(config);

            new PopulateProductsFromServerState(this.dataState.crossSellProducts).execute();

            this.isLoading = false;
        } catch (error) {
            console.warn(error);
        }
    };

    private setupParams = (config: IProductCrossSellConfig) => {
        const { items: propsItems = [], paramsById } = config;

        let items: IItem[] = propsItems;

        if (paramsById) {
            items = ProdcatApiSdk.getItemsFromParamsById(paramsById);
        }

        this.params = { items: this.getProductsFromItems(items || []) };
    };
}
